import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getPixelTags, deletePixelTag } from '@/api/pixelTagApi';
import { PixelTag } from '@/types/pixel-tag';
import { useProfile } from '@/hooks/UserHooks';
import AddEditPixelTagModal from '../components/pixel-tag/AddEditPixelTagModal';
import PixelCodeModal from '../components/pixel-tag/PixelCodeModal';
import DeleteModal from '@/components/DeleteModal';
import { toast } from 'react-toastify';
import { Loader2, Code, Edit, Trash2, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';

const PixelTagPage = () => {
    const navigate = useNavigate();
    const { userProfile } = useProfile();
    const [data, setData] = useState<PixelTag[]>([]);
    const [dataToEdit, setDataToEdit] = useState<PixelTag | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [isCodeModalOpen, setIsCodeModalOpen] = useState<boolean>(false);
    const [selectedPixelTag, setSelectedPixelTag] = useState<PixelTag | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [deleteTarget, setDeleteTarget] = useState<PixelTag | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        getPixelTags()
            .then((res) => {
                setData(res);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const toggleAddModal = () => {
        if (isAddModalOpen) {
            setDataToEdit(null);
        }
        setIsAddModalOpen(!isAddModalOpen);
    };

    const toggleCodeModal = () => {
        setIsCodeModalOpen(!isCodeModalOpen);
    };

    const handleUpdateData = (newData: PixelTag) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: PixelTag) => {
        setData([...data, newData]);
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        deletePixelTag(deleteTarget.id)
            .then(() => {
                setData(data.filter((item) => item.id !== deleteTarget.id));
                toast.success('Pixel tag removido com sucesso!');
            })
            .catch(() => {
                toast.error('Erro ao remover pixel tag');
            })
            .finally(() => {
                setIsDeleteModalOpen(false);
                setDeleteTarget(null);
            });
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const filteredData = data.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.adNetwork?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.advertiser?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.agency?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.publisher?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    document.title = 'Pixel Tags | Ad Pulse';
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Pixel Tags</h1>
                    <p className="text-muted-foreground">
                        Gere pixels de rastreamento para o anunciante implementar na landing page.
                    </p>
                </div>
                <Button onClick={() => {
                    setDataToEdit(null);
                    toggleAddModal();
                }}>
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Pixel Tag
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Filtro</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <Input
                        placeholder="Procurar..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="max-w-sm"
                    />
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex h-64 items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="flex h-64 items-center justify-center text-muted-foreground">
                            Nenhum pixel tag encontrado
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[80px]">ID</TableHead>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Ad Network</TableHead>
                                    <TableHead>Anunciante</TableHead>
                                    <TableHead>Agência</TableHead>
                                    <TableHead>Publisher</TableHead>
                                    <TableHead>Criado em</TableHead>
                                    <TableHead className="text-right">Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredData.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell className="font-medium">{row.id}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.adNetwork?.name ?? '-'}</TableCell>
                                        <TableCell>{row.advertiser?.name ?? '-'}</TableCell>
                                        <TableCell>{row.agency?.name ?? '-'}</TableCell>
                                        <TableCell>{row.publisher?.name ?? '-'}</TableCell>
                                        <TableCell>
                                            {new Date(row.createdAt).toLocaleDateString('pt-BR', dateFormatOptions)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setSelectedPixelTag(row);
                                                        toggleCodeModal();
                                                    }}
                                                    title="Ver código do pixel"
                                                >
                                                    <Code className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setDataToEdit(row);
                                                        toggleAddModal();
                                                    }}
                                                    title="Editar"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setDeleteTarget(row);
                                                        setIsDeleteModalOpen(true);
                                                    }}
                                                    title="Remover"
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <AddEditPixelTagModal
                pixelTag={dataToEdit}
                isOpen={isAddModalOpen}
                toggle={toggleAddModal}
                onAddData={handleAddData}
                onUpdateData={handleUpdateData}
            />

            <PixelCodeModal
                pixelTag={selectedPixelTag}
                isOpen={isCodeModalOpen}
                toggle={toggleCodeModal}
            />

            <DeleteModal
                show={isDeleteModalOpen}
                onCloseClick={() => setIsDeleteModalOpen(false)}
                onDeleteClick={handleDelete}
            />
        </div>
    );
};

export default PixelTagPage;
